import React, { useCallback, useRef, useState } from 'react';
import { Checkbox } from '@mui/material';
import { SimpleTabs } from 'components';
import { Images, User } from 'utils';
import { Draw } from '../../../../../fragments/schedule/core/common/draw';
import { TypeSignature } from '../../../../../fragments/schedule/core/common/typeSignature';
import { SignatureFileUploader } from '../../../../../fragments/schedule/core/common/signatureFileUploader';
import { UploadedSignature } from '../../../../../fragments/schedule/core/common/uploadedSignature';
import { signatureTabsLabels } from '../../../../../fragments/schedule/core/common/constants';

const SIGNATURE_TAB = {
    DRAW: 'Draw',
    TYPE: 'Type',
    UPLOAD: 'Upload',
};

export const FormSignature = ({ onSignatureChange, currentValue, readOnly, hasError, signerLock }) => {
    const { userInfo } = User();
    const savedSignature = userInfo?.signature;
    const hasSavedSignature = !!savedSignature?.url;
    const [activeTab, setActiveTab] = useState(SIGNATURE_TAB.DRAW);
    const [useSaved, setUseSaved] = useState(false);
    const canvasRef = useRef(null);

    const handleCanvasRef = (ref) => {
        canvasRef.current = ref;
    };

    const handleUseSavedChange = (e) => {
        const checked = e.target.checked;
        setUseSaved(checked);
        if (canvasRef.current?.current) {
            canvasRef.current.current.clear();
        }
        if (checked && savedSignature?.url) {
            // Mirror the Schedule SignaturePanel flow: don't reference the
            // stored file directly — defer to submit time, where
            // uploadPendingSignatures re-uploads a FRESH copy of the saved
            // signature and uses that upload's response. Emit the pending
            // "saved" marker (shape recognised by uploadPendingSignatures).
            onSignatureChange?.({ _pending: true, type: 'saved', savedRef: savedSignature });
        } else {
            onSignatureChange?.(null);
        }
    };

    // Capture drawn signature as base64 — no upload
    const handleDrawEnd = useCallback(() => {
        if (readOnly || !canvasRef.current?.current) return;
        const dataUrl = canvasRef.current.current.getTrimmedCanvas().toDataURL('image/png');
        if (dataUrl) {
            onSignatureChange?.({ _pending: true, type: 'draw', dataUrl });
        }
    }, [readOnly, onSignatureChange]);

    // Capture typed name — renders to canvas for base64
    const handleNameChange = useCallback((name) => {
        if (!name?.trim()) return;
        const canvas = document.createElement('canvas');
        canvas.width = 480;
        canvas.height = 120;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.font = "italic 40px 'Dancing Script', 'Brush Script MT', cursive";
        ctx.fillStyle = '#172B4D';
        ctx.textBaseline = 'middle';
        ctx.fillText(name, 20, 60);
        const dataUrl = canvas.toDataURL('image/png');
        onSignatureChange?.({ _pending: true, type: 'type', dataUrl });
    }, [onSignatureChange]);

    // Capture uploaded file — no upload here; defer to submit time where
    // uploadPendingSignatures uploads the File. Emit the pending "upload"
    // marker (shape recognised by uploadPendingSignatures).
    const handleUploadFileChange = useCallback((file) => {
        onSignatureChange?.(file ? { _pending: true, type: 'upload', file } : null);
    }, [onSignatureChange]);

    const handleClear = () => {
        if (canvasRef.current?.current) {
            canvasRef.current.current.clear();
        }
        setUseSaved(false);
        onSignatureChange?.(null);
    };

    // Check if current value is already uploaded (not pending)
    const isUploaded = currentValue && !currentValue._pending;

    const tabsContent = [
        {
            tabComponent: <Draw canvasWidth={480} onCanvasRef={handleCanvasRef} onDrawEnd={handleDrawEnd} hideSaveDefault />,
            tab: 'Draw',
        },
        {
            tabComponent: <TypeSignature onNameChange={handleNameChange} hideSaveDefault />,
            tab: 'Type',
        },
        {
            tabComponent: <SignatureFileUploader onFileChange={handleUploadFileChange} hideSaveDefault />,
            tab: 'Upload',
        },
    ];

    if (signerLock && !currentValue) {
        return (
            <div className="form-signature-locked">
                <img src={Images.lockGray} alt="locked" className="form-signature-locked-icon" />
                <div className="form-signature-locked-text">
                    <p className="form-signature-locked-name">{signerLock.name}</p>
                    <p className="form-signature-locked-desc">
                        Only the assigned signer can sign this form. You can review but not complete the signature.
                    </p>
                </div>
            </div>
        );
    }

    if (readOnly && currentValue) {
        return (
            <div>
                <UploadedSignature
                    noDel={true}
                    currentFile={{ file: currentValue }}
                    alreadyUploaded={true}
                    alwaysDelete={true}
                />
            </div>
        );
    }

    return (
        <div className="form-signature-picker">
            {hasSavedSignature && !readOnly && (
                <div className="signature-panel-saved-row">
                    <Checkbox
                        checked={useSaved}
                        onChange={handleUseSavedChange}
                        style={{ padding: 0, color: useSaved ? '#347AF0' : '#D0D5DD' }}
                    />
                    <span className="signature-panel-saved-label">Use my saved signature</span>
                    {savedSignature?.url && <img src={savedSignature.url} alt="saved" className="signature-panel-saved-preview" />}
                </div>
            )}

            {!readOnly && !useSaved && (
                <div>
                    <SimpleTabs
                        customTabPanel={'signature-tab-flow-box'}
                        getTab={setActiveTab}
                        tab={activeTab}
                        defaultTab={SIGNATURE_TAB.DRAW}
                        tabsLabels={signatureTabsLabels}
                        tabsContent={tabsContent}
                    />
                    <p className="draw-info-text">By signing electronically, I agree to the clinic's terms and conditions.</p>
                </div>
            )}

            {isUploaded && !useSaved && (
                <div style={{ marginTop: 20 }}>
                    <UploadedSignature
                        noDel={readOnly}
                        handleRemoveUploaded={handleClear}
                        currentFile={{ file: currentValue }}
                        alreadyUploaded={true}
                        alwaysDelete={true}
                    />
                </div>
            )}
        </div>
    );
};
